import {
  Badge,
  Box,
  Divider,
  Flex,
  Icon,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import Button from 'components/ui/Button';
import { ESTADO_META, formatFecha } from 'constants/episodeMetadata';
import {
  MdAdd,
  MdCheckCircle,
  MdEdit,
  MdFolderOpen,
  MdLocalHospital,
  MdMedicalServices,
  MdPlayArrow,
  MdScience,
} from 'react-icons/md';
import { ClinicalEpisode, PhysicalEvaluation } from 'types/models';

type FontSizeScale = 'sm' | 'md' | 'lg';

const FONT_SIZE_CONFIG: Record<FontSizeScale, { header: string; label: string; value: string; subtext: string }> = {
  sm: { header: '9px',  label: '7px',  value: 'xs',  subtext: 'xs' },
  md: { header: 'xs',   label: '9px',  value: 'sm',  subtext: 'xs' },
  lg: { header: 'sm',   label: '11px', value: 'md',  subtext: 'sm' },
};

export interface EpisodeCardProps {
  episode: ClinicalEpisode;
  headerless?: boolean;
  noBorder?: boolean;
  // legacy operational actions
  onDiagnoseClick?: () => void;
  onEditClick?: () => void;
  onStartTreatmentClick?: () => void;
  onCloseClick?: () => void;
  onArchiveClick?: () => void;
  onReopenClick?: () => void;
  isTransitioning?: boolean;
  onCompleteAtStep?: (step: number) => void;
  // evaluations
  evaluations?: PhysicalEvaluation[];
  onNewEvaluation?: () => void;
  onViewEvaluation?: (ev: PhysicalEvaluation) => void;
  // HU quick actions (Scenario 3)
  onViewDetailsClick?: () => void;
  onStartPlanClick?: () => void;
  // sizing
  w?: string | number;
  maxW?: string | number;
  fontSize?: FontSizeScale;
}

export default function EpisodeCard({
  episode,
  headerless = false,
  noBorder   = false,
  onDiagnoseClick,
  onEditClick,
  onStartTreatmentClick,
  onCloseClick,
  onArchiveClick,
  onReopenClick,
  isTransitioning = false,
  evaluations,
  onNewEvaluation,
  onViewEvaluation,
  onViewDetailsClick,
  onStartPlanClick,
  w,
  maxW,
  fontSize = 'lg',
}: EpisodeCardProps) {
  const fs = FONT_SIZE_CONFIG[fontSize];

  const cardBg       = useColorModeValue('white',     'navy.800');
  const borderColor  = useColorModeValue('gray.200',  'whiteAlpha.150');
  const dividerColor = useColorModeValue('gray.100',  'whiteAlpha.80');
  const mutedColor   = useColorModeValue('gray.400',  'gray.500');
  const noteColor    = useColorModeValue('gray.700',  'gray.200');
  const motivoBg     = useColorModeValue('blue.50',   'navy.750');
  const motivoBorder = useColorModeValue('blue.200',  'blue.700');
  const diagBg       = useColorModeValue('purple.50', 'navy.750');
  const diagBorder   = useColorModeValue('purple.200','purple.700');
  const emptyBorder  = useColorModeValue('gray.200',  'whiteAlpha.200');
  const labelColor   = useColorModeValue('gray.400',  'gray.500');
  const evalItemBg   = useColorModeValue('gray.50',   'navy.750');
  const evalHoverBg  = useColorModeValue('teal.50',   'teal.900');

  const cfg = ESTADO_META[episode.estado];

  const hasDiag = !!(episode.codigoCie10 || episode.diagnosticoPrincipal);
  const hasActions =
    onDiagnoseClick || onEditClick || onStartTreatmentClick ||
    onCloseClick || onArchiveClick || onReopenClick;

  return (
    <Box
      bg={cardBg}
      w={w}
      maxW={maxW}
      border={noBorder ? 'none' : '1px solid'}
      borderColor={borderColor}
      borderLeft={noBorder ? 'none' : '4px solid'}
      borderLeftColor={cfg.dot}
      borderRadius={noBorder ? 0 : '7px'}
      overflow='hidden'
      boxShadow={noBorder ? 'none' : '0 2px 8px rgba(0,0,0,0.05)'}
    >

      {/* ── HEADER: estado · HC code (left) | fecha (right) ──────────── */}
      {!headerless && (
        <Flex
          px='16px' py='10px'
          align='center' justify='space-between'
          flexWrap='wrap' gap='6px'
          borderBottom='1px solid'
          borderColor={dividerColor}
        >
          <Flex align='center' gap='8px'>
            <Box w='8px' h='8px' borderRadius='full' bg={cfg.dot} flexShrink={0} />
            <Badge
              colorScheme={cfg.colorScheme}
              borderRadius='full'
              px='10px' py='2px'
              fontSize={fs.header}
              fontWeight='700'
            >
              {cfg.label}
            </Badge>
            <Text fontFamily='mono' fontSize={fs.header} fontWeight='700' color='brand.500'>
              {episode.codigoHc}
            </Text>
          </Flex>
          <Flex align='center' gap='4px' color={mutedColor}>
            <Text fontSize={fs.header} fontWeight='600'>{formatFecha(episode.fechaApertura)}</Text>
            {episode.fechaCierre && (
              <>
                <Text fontSize={fs.header} mx='1'>→</Text>
                <Text fontSize={fs.header} fontWeight='600'>{formatFecha(episode.fechaCierre)}</Text>
              </>
            )}
          </Flex>
        </Flex>
      )}

      {/* ── BODY ─────────────────────────────────────────────────────── */}
      <Box px='14px' pt='12px' pb='14px'>

        {/* Motivo de consulta */}
        <Box
          bg={motivoBg}
          border='1px solid'
          borderColor={motivoBorder}
          borderRadius='8px'
          px='12px' py='10px'
          mb='10px'
        >
          <Text
            fontSize={fs.label}
            fontWeight='700'
            color='blue.500'
            textTransform='uppercase'
            letterSpacing='0.08em'
            mb='4px'
          >
            Motivo de consulta
          </Text>
          <Text fontSize={fs.value} fontWeight='600' color='blue.800' lineHeight='1.5' noOfLines={3}>
            {episode.motivoConsulta}
          </Text>
        </Box>

        {/* Diagnóstico / Enfermedad — CIE-10 code + texto descriptivo */}
        {hasDiag ? (
          <Box
            bg={diagBg}
            border='1px solid'
            borderColor={diagBorder}
            borderRadius='8px'
            px='12px' py='10px'
            mb='10px'
          >
            <Text
              fontSize={fs.label}
              fontWeight='700'
              color='purple.500'
              textTransform='uppercase'
              letterSpacing='0.08em'
              mb='6px'
            >
              Diagnóstico / Enfermedad
            </Text>
            {episode.codigoCie10 && (
              <Badge
                colorScheme='purple'
                borderRadius='6px'
                px='8px' py='2px'
                fontSize='11px'
                fontWeight='800'
                letterSpacing='wider'
                fontFamily='mono'
                mb='5px'
                display='block'
                w='fit-content'
              >
                {episode.codigoCie10}
              </Badge>
            )}
            <Text fontSize={fs.value} fontWeight='600' color='purple.900' lineHeight='1.5' noOfLines={3}>
              {episode.diagnosticoPrincipal ?? episode.anamnesis?.evolucion ?? '—'}
            </Text>
            {episode.diagnosticoSecundario && (
              <Text fontSize={fs.subtext} color='purple.500' mt='3px' noOfLines={1}>
                Secundario: {episode.diagnosticoSecundario}
              </Text>
            )}
          </Box>
        ) : (
          /* Empty state — sin diagnóstico registrado */
          <Flex
            align='center' gap='10px'
            px='12px' py='8px'
            mb='10px'
            borderRadius='8px'
            border='1.5px dashed'
            borderColor={onDiagnoseClick ? `${cfg.colorScheme}.200` : emptyBorder}
            bg={onDiagnoseClick ? `${cfg.colorScheme}.50` : 'transparent'}
            cursor={onDiagnoseClick ? 'pointer' : 'default'}
            onClick={onDiagnoseClick}
            role={onDiagnoseClick ? 'button' : undefined}
            _hover={onDiagnoseClick ? { borderColor: `${cfg.colorScheme}.400`, bg: `${cfg.colorScheme}.100` } : undefined}
            transition='all 0.15s'
          >
            <Icon
              as={MdLocalHospital}
              w='18px' h='18px'
              color={onDiagnoseClick ? `${cfg.colorScheme}.400` : 'gray.300'}
              flexShrink={0}
            />
            <Box>
              <Text fontSize={fs.value} fontWeight='700' color={onDiagnoseClick ? `${cfg.colorScheme}.700` : 'gray.400'}>
                Sin diagnóstico registrado
              </Text>
              {onDiagnoseClick && (
                <Text fontSize={fs.subtext} color={`${cfg.colorScheme}.400`} mt='1px'>
                  Clic para añadir diagnóstico CIE-10
                </Text>
              )}
            </Box>
          </Flex>
        )}

        {/* Nota apertura / cierre */}
        {episode.notaApertura && (
          <>
            <Divider borderColor={dividerColor} mb='8px' />
            <Box mb='10px'>
              <Text fontSize={fs.label} fontWeight='700' color={labelColor} textTransform='uppercase' letterSpacing='0.08em' mb='2px'>
                Nota de apertura
              </Text>
              <Text fontSize={fs.subtext} color={noteColor} noOfLines={2} lineHeight='1.5'>
                {episode.notaApertura}
              </Text>
            </Box>
          </>
        )}
        {episode.notaCierre && (
          <>
            <Divider borderColor={dividerColor} mb='8px' />
            <Box mb='10px'>
              <Text fontSize={fs.label} fontWeight='700' color={labelColor} textTransform='uppercase' letterSpacing='0.08em' mb='2px'>
                Nota de cierre
              </Text>
              <Text fontSize={fs.subtext} color={noteColor} noOfLines={2} lineHeight='1.5'>
                {episode.notaCierre}
              </Text>
            </Box>
          </>
        )}

        {/* Evaluaciones físicas */}
        {evaluations !== undefined && (
          <>
            <Divider borderColor={dividerColor} mb='10px' />
            <Flex align='center' justify='space-between' mb='7px'>
              <Flex align='center' gap='5px'>
                <Icon as={MdScience} color='teal.500' w='13px' h='13px' />
                <Text fontSize={fs.label} fontWeight='700' color={labelColor} textTransform='uppercase' letterSpacing='0.08em'>
                  Evaluaciones Físicas
                </Text>
                {evaluations.length > 0 && (
                  <Badge colorScheme='teal' borderRadius='full' px='6px' fontSize='9px' fontWeight='700'>
                    {evaluations.length}
                  </Badge>
                )}
              </Flex>
              {onNewEvaluation && (
                <Button
                  size='xs'
                  colorScheme='teal'
                  variant='ghost'
                  leftIcon={<Icon as={MdAdd} w='10px' h='10px' />}
                  onClick={onNewEvaluation}
                >
                  Nueva
                </Button>
              )}
            </Flex>
            {evaluations.length === 0 ? (
              <Text fontSize={fs.subtext} color={mutedColor} fontStyle='italic'>
                Sin evaluaciones registradas
              </Text>
            ) : (
              <Flex direction='column' gap='4px'>
                {evaluations.slice(0, 3).map((ev) => {
                  const romCount = ev.rangoMovimiento ? Object.keys(ev.rangoMovimiento).length : 0;
                  const posCount = ev.pruebasEspecificas
                    ? Object.values(ev.pruebasEspecificas).filter((p) => p.resultado === 'positivo').length
                    : 0;
                  const evaColor = ev.escalaDolor == null ? 'gray'
                    : ev.escalaDolor <= 3 ? 'green'
                    : ev.escalaDolor <= 6 ? 'orange' : 'red';
                  return (
                    <Flex
                      key={ev.id}
                      align='center'
                      gap='8px'
                      px='9px' py='6px'
                      borderRadius='7px'
                      bg={evalItemBg}
                      border='1px solid'
                      borderColor={dividerColor}
                      cursor={onViewEvaluation ? 'pointer' : 'default'}
                      _hover={onViewEvaluation ? { bg: evalHoverBg, borderColor: 'teal.200' } : undefined}
                      transition='all 0.12s'
                      onClick={onViewEvaluation ? () => onViewEvaluation(ev) : undefined}
                    >
                      <Flex w='20px' h='20px' borderRadius='5px' bg='teal.100' align='center' justify='center' flexShrink={0}>
                        <Text fontSize='9px' fontWeight='800' color='teal.600'>#{ev.numeroEvaluacion}</Text>
                      </Flex>
                      <Text fontSize={fs.subtext} fontWeight='600' color={noteColor} flex={1} noOfLines={1}>
                        {ev.diagnostico ?? formatFecha(ev.fechaEvaluacion)}
                      </Text>
                      <Flex gap='4px' flexShrink={0}>
                        {ev.escalaDolor != null && (
                          <Badge colorScheme={evaColor} borderRadius='full' px='5px' fontSize='9px'>
                            EVA {ev.escalaDolor}
                          </Badge>
                        )}
                        {romCount > 0 && (
                          <Badge colorScheme='blue' variant='subtle' borderRadius='full' px='5px' fontSize='9px'>
                            {romCount} ROM
                          </Badge>
                        )}
                        {posCount > 0 && (
                          <Badge colorScheme='orange' variant='subtle' borderRadius='full' px='5px' fontSize='9px'>
                            {posCount}+
                          </Badge>
                        )}
                      </Flex>
                    </Flex>
                  );
                })}
                {evaluations.length > 3 && (
                  <Text fontSize={fs.label} color={mutedColor} textAlign='center' pt='2px'>
                    +{evaluations.length - 3} más
                  </Text>
                )}
              </Flex>
            )}
          </>
        )}

        {/* ── Quick actions — Z-pattern (Scenario 3) ───────────────────
            Left  (secondary): Ver caso clínico
            Right (primary):   Definir plan de tratamiento              */}
        {(onViewDetailsClick || onStartPlanClick) && (
          <>
            <Divider borderColor={dividerColor} my='12px' />
            <Flex justify='space-between' align='center' gap='8px'>
              <Button
                size='sm'
                variant='outline'
                leftIcon={<Icon as={MdFolderOpen} />}
                onClick={onViewDetailsClick}
                isDisabled={!onViewDetailsClick}
                h='38px'
                px='16px'
                fontSize='sm'
              >
                Ver caso clínico
              </Button>
              <Button
                size='sm'
                variant='brand'
                leftIcon={<Icon as={MdMedicalServices} />}
                onClick={onStartPlanClick}
                isDisabled={!onStartPlanClick}
                h='38px'
                px='16px'
                fontSize='sm'
              >
                Definir plan de tratamiento
              </Button>
            </Flex>
          </>
        )}

        {/* ── Legacy operational action bar (Diagnóstico, Actualizar, etc.) */}
        {hasActions && (
          <>
            <Divider borderColor={dividerColor} my='10px' />
            <Flex gap='8px' flexWrap='wrap' justify='space-between' align='center'>
              <Flex gap='8px' flexWrap='wrap'>
                {onDiagnoseClick && (
                  <Button size='sm' colorScheme='teal' variant='outline' leftIcon={<Icon as={MdLocalHospital} />} onClick={onDiagnoseClick}>
                    Diagnóstico
                  </Button>
                )}
                {onEditClick && (
                  <Button size='sm' variant='outline' leftIcon={<Icon as={MdEdit} />} onClick={onEditClick}>
                    Actualizar
                  </Button>
                )}
                {onStartTreatmentClick && (
                  <Button size='sm' colorScheme='orange' variant='outline' leftIcon={<Icon as={MdPlayArrow} />} isLoading={isTransitioning} onClick={onStartTreatmentClick}>
                    Iniciar Tratamiento
                  </Button>
                )}
                {onArchiveClick && (
                  <Button size='sm' colorScheme='gray' variant='outline' isLoading={isTransitioning} onClick={onArchiveClick}>
                    Archivar
                  </Button>
                )}
                {onReopenClick && (
                  <Button size='sm' colorScheme='blue' variant='outline' isLoading={isTransitioning} onClick={onReopenClick}>
                    Reabrir episodio
                  </Button>
                )}
              </Flex>
              {onCloseClick && (
                <Button size='sm' colorScheme='green' leftIcon={<Icon as={MdCheckCircle} />} onClick={onCloseClick} boxShadow='sm'>
                  Dar de Alta
                </Button>
              )}
            </Flex>
          </>
        )}

      </Box>
    </Box>
  );
}
